import React, { FC } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Image from 'react-bootstrap/Image';

import logo from 'assets/ehr-logo-full.svg';
import authBg from 'assets/auth-bg.svg';

import styled from 'styled-components';
import { getSubdomain } from 'utils/helpers/routing';
import tenantLogoConfig from 'utils/tenantLogoConfig';
import { Panel, Content } from './styled/Panel';
import Heading from './styled/Heading';
import LanguageSwitch from './LanguageSwitch';

interface IProps {
  title: string;
  children?: React.ReactNode;
}

const AuthLayout: FC<IProps> = ({ title, children }: IProps) => {
  const isOperatorMode = getSubdomain() === 'operators';
  const subdomain = getSubdomain();

  const renderLogo = () => {
    if (tenantLogoConfig[subdomain]) {
      const { logoFile, showInsteadOfOriginalLogo } = tenantLogoConfig[subdomain];

      if (!showInsteadOfOriginalLogo) {
        return (
          <LogoContainer>
            <Image
              src={logo}
              height={110}
              alt="Ehr logo"
            />
            <Image
              className="mr-3 ml-3"
              src={logoFile}
              alt="logo"
            />
          </LogoContainer>
        );
      }

      return (
        <LogoContainer>
          <Image
            className="mt-4"
            src={logoFile}
            alt="logo"
          />
        </LogoContainer>
      );
    }

    return (
      <Image
        src={logo}
        width={220}
        alt="Ehr logo"
      />
    );
  };

  return (
    <Wrapper>
      {process.env.REACT_APP_SHOW_LANG_MENU === 'true' && !isOperatorMode && (
        <LangSwitchHolder>
          <LanguageSwitch />
        </LangSwitchHolder>
      )}
      <Container>
        <Row>
          <Col
            md={{ span: 6, offset: 3 }}
            lg={{ span: 4, offset: 4 }}
            className="text-center"
          >
            {renderLogo()}
          </Col>
        </Row>
        <Row>
          <Col
            md={{ span: 6, offset: 3 }}
            lg={{ span: 4, offset: 4 }}
            className="mt-4 mb-4"
          >
            <Panel>
              <Heading className="text-center">
                <Container fluid>
                  <Row>
                    <Col>
                      <h2 className="h4 mb-0">{title}</h2>
                    </Col>
                  </Row>
                </Container>
              </Heading>
              <Content>
                <Container fluid>
                  <Row>
                    <Col>{children}</Col>
                  </Row>
                </Container>
              </Content>
            </Panel>
          </Col>
        </Row>
      </Container>
    </Wrapper>
  );
};

export default AuthLayout;

const Wrapper = styled.div`
  position: relative;
  padding-top: 2rem;
  background: transparent url(${authBg}) no-repeat center center;
  background-size: cover;
  min-height: 100vh;
`;

const LangSwitchHolder = styled.div`
  position: absolute;
  top: 2rem;
  right: 4rem;
  z-index: 1;

  @media ${props => props.theme.breakpoints.md} {
    top: 1rem;
    right: 1rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  // When the second logo wraps, this will give it a margin at the top
  & > img + img {
    margin-top: 1rem;
    max-height: 12rem;
    max-width: 100%;
  }

  & > img {
    max-height: 12rem;
    max-width: 100%;
  }

  & > img:nth-child(2) {
    max-height: 12rem;
    max-width: 100%;
  }
`;
